import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, useTheme } from '@mui/material'; // Removed Link
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTransactionService } from '../../../services/transaction.service';
import { TransactionIssueType } from '../../../services/transaction.types';

const TaxReportWarningBanner: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const transactionService = useTransactionService();
    const isDarkMode = theme.palette.mode === 'dark';

    // Dark mode uses a darker background with bright text
    // Light mode uses a light blue background with dark blue text
    const styles = {
        backgroundColor: isDarkMode ? '#1e3a5f' : '#e3f2fd',
        textPrimary: isDarkMode ? '#ffffff' : theme.palette.text.primary,
        iconColor: isDarkMode ? '#bbdefb' : '#2196f3',
    };

    // Get issue counts for filtering
    const {
        data: issueCountData = {
            missingAssetValueCount: 0,
            costBasisCount: 0,
            spamCoinCount: 0,
            allTimeTotalTransactions: 0,
        },
        isLoading: isIssueCountLoading,
    } = useQuery({
        queryKey: ['allTimeIssueCount'],
        queryFn: () => transactionService.getAllTimeIssueCount(),
    });

    const allTimeTotalTransactions = issueCountData?.allTimeTotalTransactions || 0;

    // Calculate warning count (only missingAssetValueCount)
    const warningCount = useMemo(() => {
        return (issueCountData?.missingAssetValueCount || 0);
    }, [issueCountData]);

    const handleNavigateToTransactions = () => {
        // Only include flags for issues that are present in the data
        const flagsToInclude = [];

        if (issueCountData.missingAssetValueCount > 0) {
            flagsToInclude.push(TransactionIssueType.MissingPrice);
        }

        if (issueCountData.costBasisCount > 0) {
            flagsToInclude.push(TransactionIssueType.MissingCostBasis);
        }

        // Navigate to transactions page with filters for only the issues that are present
        if (flagsToInclude.length > 0) {
            navigate(`/transactions?flags=${flagsToInclude.join(',')}`);
        } else {
            // If no specific issues, just navigate to transactions page
            navigate('/transactions');
        }
    };

    // Only render the banner if there are warnings and transactions
    if (warningCount <= 0 || allTimeTotalTransactions === 0 || isIssueCountLoading) {
        return null;
    }

    return (
        <Box sx={{ width: '100%', mb: 2 }}>
            <Alert
                severity="info"
                icon={<InfoOutlinedIcon />}
                sx={{
                    backgroundColor: styles.backgroundColor,
                    color: styles.textPrimary,
                    minHeight: 54,
                    padding: 0,
                    '.MuiAlert-icon': {
                        color: styles.iconColor,
                        marginRight: 1,
                        padding: '12px 0',
                        paddingLeft: 2,
                        alignItems: 'center',
                    },
                    '.MuiAlert-message': {
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                        component="span"
                        onClick={handleNavigateToTransactions}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'baseline',
                            cursor: 'pointer',
                            fontSize: '0.95rem',
                            fontWeight: 'medium',
                            color: styles.textPrimary,
                            whiteSpace: 'nowrap',
                            textDecoration: 'none',
                            outline: 'none',
                            '&:focus': { outline: 'none' },
                            '&:hover > .linked-text-part': {
                                color: isDarkMode ? '#e3f2fd' : theme.palette.primary.main,
                            },
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleNavigateToTransactions();
                            }
                        }} // Accessibility: trigger on Space/Enter
                    >
                        <Trans
                            i18nKey="taxReports.warningBanner.fullTextWithLink"
                            values={{
                                mainText: t('taxReports.warningBanner.checkTransactions'),
                                linkText: t('taxReports.warningBanner.checkTransactionsRight'),
                            }}
                            components={{
                                1: (
                                    <Box
                                        component="span"
                                        className="linked-text-part"
                                        sx={{
                                            ml: 0.5,
                                            textDecoration: 'underline',
                                            fontWeight: 'bold',
                                        }}
                                    />
                                ),
                            }}
                        />
                    </Box>
                </Box>
            </Alert>
        </Box>
    );
};

export default TaxReportWarningBanner;
